import { Alert } from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

import { payoutOrdersApi } from 'api';
import {
  CloseFormikDialogResult,
  Dialog,
  FormControls,
  FormikYesNoRadioGroup,
  CloseDialogHandler,
} from 'components';
import {
  AppConfigArea,
  AppConfigKey,
  PayoutOrderStatus,
  QueryKey,
} from 'enums';
import { useAppConfigValue, usePrevious, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PayoutOrder, ResolvePayoutOrderDispute } from 'types';

import { ManageReceipts } from '../ManageReceipts';

type Values = {
  paymentSent: boolean;
};

type Props = {
  open: boolean;
  order: PayoutOrder;
  disabled?: boolean;
  onClose: CloseDialogHandler<ResolvePayoutOrderDispute>;
};

export const ResolveOrderDialog: React.FC<Props> = ({
  open,
  order,
  disabled,
  onClose,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.resolve',
  });

  const { role } = useUser();
  const prevOpen = usePrevious(open);
  const [initialValues, setInitialValues] = useState<Values>({
    paymentSent: true,
  });

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        paymentSent: Yup.boolean().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const queryReceiptsKey = useMemo(
    () => [QueryKey.PayoutOrderReceipts, order?.id],
    [order],
  );
  const queryReceiptsEnabled = useMemo(
    () => open && !!order?.id && !order.p2pProviderOrderId,
    [order, open],
  );
  const queryResultReceipts = useQuery(
    queryReceiptsKey,
    () => payoutOrdersApi.getOrderReceiptsAsRole(role)(order.id),
    { enabled: queryReceiptsEnabled },
  );

  const receiptsConfigQueryResult = useAppConfigValue({
    area: AppConfigArea.PayoutOrders,
    key: AppConfigKey.Receipts,
  });

  const okDisabled = useMemo(() => {
    if (order.p2pProviderOrderId) {
      return false;
    }

    return (
      isEmpty(queryResultReceipts.data) &&
      (receiptsConfigQueryResult?.data as any)?.managerReceiptRequired
    );
  }, [
    order.p2pProviderOrderId,
    queryResultReceipts.data,
    receiptsConfigQueryResult?.data,
  ]);

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      // TODO: reset on success also
      if (!result.ok) {
        result.data?.formikHelpers.resetForm();
      }
      if (result.data?.values) {
        const { values } = result.data;
        onClose({
          ok: result.ok,
          data: {
            status: values.paymentSent
              ? PayoutOrderStatus.Completed
              : PayoutOrderStatus.Cancelled,
          },
        });
      }
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const renderOrderStatus = useCallback(
    (status: string, prefix?: string) => (
      <span>
        {`${prefix || t('resolve_as.prefix')} `}
        <span className="tw-font-bold">{status}</span>
      </span>
    ),
    [t],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const renderAlert = useCallback(() => {
    if (formik.values.paymentSent) {
      return renderOrderStatus(t('resolve_as.completed'));
    } else {
      return renderOrderStatus(t('resolve_as.cancelled'));
    }
  }, [formik, renderOrderStatus, t]);

  useEffect(() => {
    if (open && !prevOpen) {
      setInitialValues({ paymentSent: true });
    }
  }, [open, prevOpen, order]);

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('title_active')}
        data={{ values: formik.values, formikHelpers: formik }}
        okDisabled={formik.values.paymentSent && !!okDisabled}
        onClose={handleClose}
      >
        <Form>
          <Alert severity="info" sx={{ mb: 4, py: 0 }}>
            {renderAlert()}
          </Alert>
          <FormControls>
            <FormikYesNoRadioGroup
              name="paymentSent"
              disabled={disabled}
              label={t('payment_sent')}
            />
          </FormControls>
          {!order?.p2pProviderOrderId && formik.values.paymentSent && (
            <ManageReceipts
              queryResultReceipts={queryResultReceipts}
              order={order}
            />
          )}
        </Form>
      </Dialog>
    </FormikProvider>
  );
};
